import type { PlacementStrategyId } from '../model/recipe';
import { findRegions, type Mask } from './stencil/mask';

/**
 * Placement strategies decide *where* the next arrow goes during reverse construction.
 *
 * Each strategy is a weight function over the currently placeable cells. Weights are
 * non-negative integers so selection stays in integer arithmetic, and every strategy is
 * pure: given the same construction state it always returns the same weights.
 */

export interface CellStatics {
  readonly width: number;
  readonly height: number;
  /** Manhattan distance from the grid centre, doubled to keep it integral. */
  readonly centreDistance: Int32Array;
  readonly maxCentreDistance: number;
  /** Distance to the nearest inactive cell or grid border. */
  readonly borderDistance: Int32Array;
  /** Number of active orthogonal neighbours (mask narrowness). */
  readonly activeNeighbours: Int32Array;
  readonly componentOf: Int32Array;
  readonly componentCount: number;
}

export function computeCellStatics(mask: Mask): CellStatics {
  const { width, height, cells } = mask;
  const centreDistance = new Int32Array(cells.length);
  const activeNeighbours = new Int32Array(cells.length);
  const borderDistance = new Int32Array(cells.length);

  const centreX = width - 1;
  const centreY = height - 1;
  let maxCentreDistance = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      const distance = Math.abs(2 * x - centreX) + Math.abs(2 * y - centreY);
      centreDistance[index] = distance;
      if (cells[index] === 1 && distance > maxCentreDistance) maxCentreDistance = distance;

      if (cells[index] === 1) {
        let neighbours = 0;
        if (x > 0 && cells[index - 1] === 1) neighbours += 1;
        if (x + 1 < width && cells[index + 1] === 1) neighbours += 1;
        if (y > 0 && cells[index - width] === 1) neighbours += 1;
        if (y + 1 < height && cells[index + width] === 1) neighbours += 1;
        activeNeighbours[index] = neighbours;
      }
    }
  }

  // Chebyshev-style distance transform to the silhouette edge: two sweeps, integer only.
  const LARGE = 1 << 20;
  for (let i = 0; i < cells.length; i += 1) borderDistance[i] = cells[i] === 1 ? LARGE : 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      if (borderDistance[index] === 0) continue;
      let best = LARGE;
      if (x === 0 || y === 0) best = 1;
      if (x > 0) best = Math.min(best, borderDistance[index - 1] + 1);
      if (y > 0) best = Math.min(best, borderDistance[index - width] + 1);
      borderDistance[index] = best;
    }
  }
  for (let y = height - 1; y >= 0; y -= 1) {
    for (let x = width - 1; x >= 0; x -= 1) {
      const index = y * width + x;
      if (cells[index] === 0) continue;
      let best = borderDistance[index];
      if (x === width - 1 || y === height - 1) best = Math.min(best, 1);
      if (x + 1 < width) best = Math.min(best, borderDistance[index + 1] + 1);
      if (y + 1 < height) best = Math.min(best, borderDistance[index + width] + 1);
      borderDistance[index] = best;
    }
  }

  const regions = findRegions(mask);
  return {
    width,
    height,
    centreDistance,
    maxCentreDistance,
    borderDistance,
    activeNeighbours,
    componentOf: regions.labels,
    componentCount: regions.sizes.length,
  };
}

/** The construction facts a strategy may look at. */
export interface StrategyContext {
  readonly statics: CellStatics;
  readonly occupied: Uint8Array;
  /** Arrows placed per mask component so far. */
  readonly componentCounts: Int32Array;
  /** Free orthogonal neighbours of a cell; a cheap measure of how boxed in it is. */
  readonly freeNeighbours: (cellIndex: number) => number;
  readonly placedCount: number;
}

export type CellWeightFn = (cellIndex: number, context: StrategyContext) => number;

function occupiedNeighbours(cellIndex: number, context: StrategyContext): number {
  const { width, height } = context.statics;
  const x = cellIndex % width;
  const y = (cellIndex - x) / width;
  let count = 0;
  if (x > 0 && context.occupied[cellIndex - 1] === 1) count += 1;
  if (x + 1 < width && context.occupied[cellIndex + 1] === 1) count += 1;
  if (y > 0 && context.occupied[cellIndex - width] === 1) count += 1;
  if (y + 1 < height && context.occupied[cellIndex + width] === 1) count += 1;
  return count;
}

/** Occupied cells within a small radius; approximates "distance to the nearest arrow". */
function nearbyOccupied(cellIndex: number, context: StrategyContext, radius: number): number {
  const { width, height } = context.statics;
  const x = cellIndex % width;
  const y = (cellIndex - x) / width;
  let count = 0;
  for (let dy = -radius; dy <= radius; dy += 1) {
    const ny = y + dy;
    if (ny < 0 || ny >= height) continue;
    for (let dx = -radius; dx <= radius; dx += 1) {
      const nx = x + dx;
      if (nx < 0 || nx >= width) continue;
      if (context.occupied[ny * width + nx] === 1) count += 1;
    }
  }
  return count;
}

const STRATEGIES: Record<PlacementStrategyId, CellWeightFn> = {
  uniform: () => 1,

  frontier: (cellIndex, context) => {
    if (context.placedCount === 0) return 1;
    return 1 + occupiedNeighbours(cellIndex, context) * 6;
  },

  'center-out': (cellIndex, context) => {
    const { centreDistance, maxCentreDistance } = context.statics;
    return 1 + Math.max(0, maxCentreDistance - centreDistance[cellIndex]);
  },

  'perimeter-in': (cellIndex, context) => {
    const distance = context.statics.borderDistance[cellIndex];
    return 1 + Math.max(0, 8 - distance) * 3;
  },

  corridor: (cellIndex, context) => {
    // Narrow parts of the silhouette have fewer active neighbours.
    const neighbours = context.statics.activeNeighbours[cellIndex];
    return 1 + (4 - neighbours) * 5;
  },

  'component-aware': (cellIndex, context) => {
    const component = context.statics.componentOf[cellIndex];
    if (component < 0) return 1;
    let max = 0;
    for (let i = 0; i < context.componentCounts.length; i += 1) {
      if (context.componentCounts[i] > max) max = context.componentCounts[i];
    }
    return 1 + (max - context.componentCounts[component]);
  },

  'depth-biased': (cellIndex, context) => {
    // Tight spots are where dependencies build up: starting an arrow in a cell that is
    // already hemmed in produces longer chains than starting one in open space.
    const free = context.freeNeighbours(cellIndex);
    return 1 + (4 - free) * 6;
  },

  'near-symmetric': (cellIndex, context) => {
    const { width, height } = context.statics;
    const x = cellIndex % width;
    const y = (cellIndex - x) / width;
    const mirrorIndex = y * width + (width - 1 - x);
    const verticalMirrorIndex = (height - 1 - y) * width + x;
    let weight = 1;
    if (context.occupied[mirrorIndex] === 1) weight += 12;
    if (context.occupied[verticalMirrorIndex] === 1) weight += 4;
    return weight;
  },

  'sparse-islands': (cellIndex, context) => {
    const nearby = nearbyOccupied(cellIndex, context, 2);
    return nearby === 0 ? 12 : Math.max(1, 6 - nearby);
  },
};

export function getCellWeightFn(strategy: PlacementStrategyId): CellWeightFn {
  const fn = STRATEGIES[strategy];
  if (fn === undefined) throw new Error(`Unknown placement strategy: ${strategy}`);
  return fn;
}
